import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';

// Per-letter reveal for the "Allure," word — runs once on mount.
const ALLURE_LETTERS = ['A', 'l', 'l', 'u', 'r', 'e', ','];
const LETTER_BASE_DELAY = 0.32;
const LETTER_STAGGER = 0.09;
const LETTER_TOTAL = LETTER_BASE_DELAY + ALLURE_LETTERS.length * LETTER_STAGGER;

export default function GuestInfoPage() {
  const navigate = useNavigate();
  const handleContinue = () => {
    // Fresh guest signups arrive from /select-role → /guest-info without
    // an account yet. Send them through /register first; VerifyEmailPage
    // routes them to /guest/home after email verification. Existing
    // guests jumping back to this page just go home.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      navigate('/register');
      return;
    }
    navigate('/guest/home', { replace: true });
  };
  const handleBack = () => {
    // Back always returns to the role picker — this page is an information
    // step that sits between /select-role and /register, so tapping the
    // arrow must never drop the user into the guest app.
    navigate('/select-role');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell"
      data-testid="guest-info-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--surface)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
      }}
    >
      <motion.button
        onClick={handleBack}
        aria-label="Go back"
        data-testid="guest-info-back"
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
        whileTap={{ scale: 0.92 }}
        className="premium-pill-button"
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 18px)',
          left: 18,
          width: 42,
          height: 42,
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
      </motion.button>

      {/* Middle stack: logo + card sits high on the page, button follows close underneath */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 26,
          marginTop: 72,
        }}
      >
        <RouteTransitionLogo width={112} height={100} dataTestId="guest-info-shared-logo" />

        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="premium-card"
          data-testid="guest-info-card-0"
          style={{
            width: '100%',
            padding: '36px 26px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {/* "Allure," — letter-by-letter blur-in reveal */}
          <p
            aria-label="Allure,"
            data-testid="guest-info-allure-text"
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 30,
              fontWeight: 400,
              color: 'var(--text)',
              lineHeight: 1.3,
              margin: 0,
              textAlign: 'center',
              display: 'inline-block',
              letterSpacing: '0.5px',
            }}
          >
            {ALLURE_LETTERS.map((letter, idx) => (
              <motion.span
                key={`${letter}-${idx}`}
                initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: 0.5,
                  ease: [0.22, 1, 0.36, 1],
                  delay: LETTER_BASE_DELAY + idx * LETTER_STAGGER,
                }}
                style={{ display: 'inline-block', whiteSpace: 'pre' }}
                aria-hidden="true"
              >
                {letter}
              </motion.span>
            ))}
          </p>

          {/* Accent underline draws once the word finishes writing */}
          <motion.span
            aria-hidden="true"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
              delay: LETTER_TOTAL + 0.05,
            }}
            style={{
              display: 'block',
              width: 46,
              height: 2,
              marginTop: 8,
              borderRadius: 2,
              background:
                'linear-gradient(90deg, transparent 0%, var(--accent-text-soft, #B02A30) 50%, transparent 100%)',
              transformOrigin: 'center',
            }}
          />

          {/* Italic tagline — rises after the underline settles */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.65,
              ease: [0.22, 1, 0.36, 1],
              delay: LETTER_TOTAL + 0.35,
            }}
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 21,
              fontWeight: 400,
              color: 'var(--text)',
              lineHeight: 1.45,
              margin: '16px 0 0',
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            the missing piece between staying somewhere and truly experiencing it.
          </motion.p>
        </motion.div>
      </div>

      {/* Footer action — follows close under the card */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 36,
          marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
            delay: LETTER_TOTAL + 0.85,
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleContinue}
          data-testid="guest-info-continue"
          className="premium-pill-button"
          style={{
            width: 240,
            padding: '15px 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 19,
            fontWeight: 700,
            letterSpacing: '0.3px',
            cursor: 'pointer',
          }}
        >
          Get started
        </motion.button>
      </div>
    </motion.div>
  );
}
